import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
	faAward, faBook, faBullhorn, faCakeCandles, faChalkboardUser, faCoins, faEye, faFlagUsa, faGraduationCap, faHandSparkles, faHotel,
	faMartiniGlassCitrus, faMicrochip, faMobileScreenButton, faMugHot, faPaintbrush, faScissors, faSeedling, faSnowflake, faSpa, faSprayCanSparkles, faUtensils
} from "@fortawesome/free-solid-svg-icons";

import horarioBonito from "../../../../utils/horarioBonito";
import { cursoCorto, sinAcentos } from "../../../../utils/cursoCorto";
import { nombreDeProfesor } from "../../../../utils/nombreCorto";
import { montoBonito } from "../../../../utils/monto";
import { Clase } from "./calendarioApi";

/*
	Cómo se ve una clase en el Calendario (Esteban, 2026-11-01):
	  - el NOMBRE DEL CURSO en grande con su icono; el módulo no sale (solo en Instituto Fundación, donde el grado es lo que distingue);
	  - la hora es la del nombre del horario, con el formato de WAPP; si el nombre no trae horas que se puedan leer, sale como está.
*/

export const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
export const DIAS_CORTOS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
export const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export type JornadaKey = "M" | "D" | "T" | "N";
/** Jornadas en décimas de hora: [desde, hasta). */
export const JORNADAS: { key: JornadaKey; nombre: string; rango: string; desde: number; hasta: number }[] = [
	{ key: "M", nombre: "Mañana", rango: "7:00 am – 11:00 am", desde: 0, hasta: 110 },
	{ key: "D", nombre: "Mediodía", rango: "11:00 am – 3:00 pm", desde: 110, hasta: 150 },
	{ key: "T", nombre: "Tarde", rango: "3:00 pm – 7:00 pm", desde: 150, hasta: 190 },
	{ key: "N", nombre: "Noche", rango: "7:00 pm – 10:00 pm", desde: 190, hasta: 240 },
];

const ICONOS: [RegExp, IconDefinition][] = [
	[/barber/, faScissors], [/acril|unas|manicur|pedicur/, faHandSparkles], [/estilis|peluquer/, faSprayCanSparkles], [/maquill/, faPaintbrush],
	[/cejas|pestan/, faEye], [/spa|facial|masaj|estetica/, faSpa], [/gastronom|cocina|chef/, faUtensils], [/pasteler|reposter|panader/, faCakeCandles],
	[/refriger|aire acond/, faSnowflake], [/ingles/, faFlagUsa], [/instituto|bachiller|grado/, faGraduationCap], [/mixolog|barten|bebida/, faMartiniGlassCitrus],
	[/marketing|redes/, faBullhorn], [/finanz/, faCoins], [/celular/, faMobileScreenButton], [/electronic|inverter/, faMicrochip],
	[/barism|cafeter/, faMugHot], [/hoteler/, faHotel], [/agronegoc|agricol/, faSeedling],
	[/taller|personaliz|demostrat/, faChalkboardUser], [/graduaci/, faAward],
];

export const iconoCurso = (c: Clase): IconDefinition => {
	const n = sinAcentos(c.curso?.nombre || c.modulo?.nombre || "");
	return (ICONOS.find(([re]) => re.test(n)) || [null, faBook])[1];
};

const esInstituto = (c: Clase) => /instituto/i.test(c.curso?.nombre || "");

export const tituloClase = (c: Clase): string => (esInstituto(c) ? c.modulo?.nombre || "" : cursoCorto(c.curso?.nombre) || c.modulo?.nombre || c.nombre);

export const subClase = (c: Clase): string => (esInstituto(c) ? "Instituto Fundación · " : "") + (c.periodo?.nombre || "");

const DIA_INICIAL = /^(Lunes|Martes|Miércoles|Jueves|Viernes|Sábado|Domingo|Sábados|Domingos)\b/;

/** «11:00 am – 3:00 pm · Sección A». Con el día delante si se pide (panel, globito de la semana). */
export const horaClase = (c: Clase, conDia = false): string => {
	const partes = horarioBonito(c.nombre).split(" · ");
	if (!conDia && partes.length > 1 && DIA_INICIAL.test(partes[0])) partes.shift();
	return partes.join(" · ").replace(/ /g, " ");
};

const HORAS = /(\d{1,2}):(\d\d) (am|pm) – (\d{1,2}):(\d\d) (am|pm)/;
const decimas = (h: string, m: string, ap: string) => ((Number(h) % 12) + (ap === "pm" ? 12 : 0)) * 10 + Math.round(Number(m) / 6);

/** [inicio, fin] en décimas leídos del nombre; si el nombre no trae horas, los de la sesión. */
export const tramoClase = (c: Clase): [number, number] => {
	const m = HORAS.exec(horarioBonito(c.nombre));
	return m ? [decimas(m[1], m[2], m[3]), decimas(m[4], m[5], m[6])] : [c.inicio, c.fin];
};

export const horaInicio = (c: Clase): string => {
	const m = HORAS.exec(horarioBonito(c.nombre));
	if (m) return `${m[1]}:${m[2]} ${m[3]}`;
	const h = Math.floor(c.inicio / 10), mm = (c.inicio % 10) * 6;
	return `${h % 12 === 0 ? 12 : h % 12}:${String(mm).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
};

export const jornadaDe = (c: Clase): JornadaKey => {
	const ini = tramoClase(c)[0];
	return (JORNADAS.find((j) => ini >= j.desde && ini < j.hasta) || JORNADAS[3]).key;
};

/** ¿La clase ocupa el aula en esa jornada? (cualquier traslape cuenta: 12–4 pm ocupa Mediodía y Tarde). */
export const ocupaJornada = (c: Clase, k: JornadaKey): boolean => {
	const [a, b] = tramoClase(c);
	const j = JORNADAS.find((x) => x.key === k)!;
	return a < j.hasta && b > Math.max(j.desde, 70);
};

export const maestraDe = (c: Clase): string => (c.maestra ? nombreDeProfesor(c.maestra.nombre, c.maestra.apellido, c.maestra.mostrar) : "");

export const bajoMinimo = (c: Clase): boolean => c.minimo > 0 && c.activos < c.minimo;
export const sinPasar = (c: Clase): boolean => !!c.lista && !c.lista.pasada && c.activos > 0;

/** Honduras y Online cobran en lempiras; México en pesos. */
export const dinero = (valor: number, countryId: number | null): string => (countryId === 2 ? "L " : "$") + montoBonito(valor).replace(/\.00$/, "");

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const fechaDe = (s: string): Date => new Date(s + "T12:00:00");
export const sumarDias = (s: string, n: number): string => { const d = fechaDe(s); d.setDate(d.getDate() + n); return iso(d); };
export const lunesDe = (s: string): string => { const d = fechaDe(s); return sumarDias(s, -((d.getDay() + 6) % 7)); };
export const diaSemana = (s: string): number => (fechaDe(s).getDay() + 6) % 7;
export const fechaLarga = (s: string): string => { const d = fechaDe(s); return `${DIAS[diaSemana(s)]} ${d.getDate()} de ${MESES[d.getMonth()]}`; };
export const fechaCorta = (s: string): string => { const d = fechaDe(s); return `${d.getDate()} ${MESES[d.getMonth()].slice(0, 3)}`; };

/*
	Buscador del Calendario y de Ocupación: cada palabra tiene que aparecer; los números cuentan solo como número entero
	(«aula 2» encuentra el Aula 2, no todo lo que dice 2026 o 2:30).
*/
export const coincideCalendario = (busqueda: string, ...textos: (string | null | undefined)[]): boolean => {
	const palabras = sinAcentos(busqueda).split(/\s+/).filter(Boolean);
	if (palabras.length < 1) return true;
	const pajar = sinAcentos(textos.filter(Boolean).join(" "));
	const numeros = new Set(pajar.match(/\d+/g) || []);
	return palabras.every((p) => (/^\d+$/.test(p) ? numeros.has(String(Number(p))) || numeros.has(p) : pajar.includes(p)));
};
